import { ContributionPeriodicity } from '../../../types/enums';
import { ContributionTypeEnum } from './ContributionData';

export enum UserGenderEnum {
	MEN = 'V',
	WOMEN = 'M'
}

export enum UserStatusType {
	ACTIVE = 'active',
	INACTIVE = 'non-active',
	DELETED = 'deleted'
}

export enum UserEmploymentStatus {
	CURRENTLY_EMPLOYED = 'CURRENTLY_EMPLOYED',
	TERMINATED = 'TERMINATED',
	INDIVIDUAL = 'INDIVIDUAL'
}

export enum UserPlanStatus {
	ACTIVE_IN_ARCA = 'ACTIVE_IN_ARCA',
	TRANSFERRED_TO_ANOTHER = 'TRANSFERRED_TO_ANOTHER',
	YET_NO_PLAN = 'YET_NO_PLAN'
}

export enum LeaveReasonType {
	USER_REJECTED_PLAN = 'USER_REJECTED_PLAN',
	USER_LEFT_COMPANY = 'USER_LEFT_COMPANY',
	ADMIN_TERMINATED = 'ADMIN_TERMINATED'
}

/**
 * Enum for employee categories
 */
export enum EmployeeCategory {
	DIRECTOR = 'DIRECTOR',
	NON_DIRECTOR = 'NON_DIRECTOR',
	TECHNICAL_DIRECTOR = 'TECHNICAL_DIRECTOR'
}

// FIXME START Se podría dejar un solo interface pero habría que ir archivo a archivo cambiandolo todo.
export interface UserI {
	id: string;
	createdAt: string;
	updatedAt: string;
	email: string;
	password: string;
	name: string;
	phone: string;
	/** Correo personal, para seguir contactando con el usuario tras dejar su empresa */
	personalEmail: string | null;
	roles: string[];
	firstSurname: string;
	lastSurname: string;
	gender: UserGenderEnum;
	language: string;
	nationality: string;
	country: string;
	nif: string;
	birthdate: string;
	street: string;
	zip: string;
	province: string;
	population: string;
	bankAccount: string;
	professionalActivity: string;
	lastLogin: string;
	status: string;
	companyId: string;
	representsCompany: boolean;
	representsParticipant: boolean;
	completedOnboarding: boolean;
	grossAnnualSalary: number;
	noWill: boolean;
	employmentStatus: UserEmploymentStatus;
	planStatus: UserPlanStatus;
	isIndividual: boolean;
	isFreelance?: boolean;
	employeeCategory?: 'DIRECTOR' | 'NON_DIRECTOR' | 'TECHNICAL_DIRECTOR';
	completedProfile?: boolean;
	profileCompletedAt?: string;
	onboardingCompletedAt?: string;
	rejectedPlanAt?: string;
	employmentStartDate?: string;
	companyLeaveDate?: string;
	leaveReason?: LeaveReasonType;
	data?: {
		showExtendedOnboarding?: boolean;
	};
}
//FIXME END

export interface UserEppConfigData {
	pensionContribution: number;
	contributionPeriodicity: ContributionPeriodicity;
	employeeContributionType: ContributionTypeEnum;
}

export interface FreelanceEppConfigData {
	fondo: string;
	plan: string;
	subplan: string;
	pensionContribution: number;
	contributionPeriodicity: ContributionPeriodicity;
	freelanceContributionType: ContributionTypeEnum;
	freelanceContributionDay: number;
	freelanceContributionPeriodicity: ContributionPeriodicity;
}

export interface CurrentUserI {
	user: {
		id: string;
		createdAt: string;
		updatedAt: string;
		isFreelance: boolean;
		email: string;
		password: string;
		name: string;
		phone: string;
		personalEmail?: string;
		roles: string[];
		firstSurname: string;
		lastSurname: string;
		gender: UserGenderEnum;
		language: string;
		nationality: string;
		country: string;
		nif: string;
		birthdate: string;
		street: string;
		zip: string;
		province: string;
		population: string;
		bankAccount: string;
		professionalActivity: string;
		lastLogin: string;
		status: string;
		companyId: string;
		representsCompany: boolean;
		representsParticipant: boolean;
		completedOnboarding: boolean;
		completedProfile: boolean;
		grossAnnualSalary: number;
		lastMovementsUpdate: string;
		noWill: boolean;
		employmentStatus: UserEmploymentStatus;
		planStatus: UserPlanStatus;
		isIndividual: boolean;
		/**
		 * Categoría que el admin fijó al invitar, mientras el usuario NO tiene plan
		 * (tarea 06/15 de comparsa). En cuanto acepta, el backend la copia a la
		 * `userEppConfig` y deja esta a `null`: no-null ⇔ invitado sin plan.
		 */
		employeeCategory?: EmployeeCategory | null;
		employmentStartDate?: string;
		profileCompletedAt?: string;
		onboardingCompletedAt?: string;
		rejectedPlanAt?: string;
		data?: {
			showExtendedOnboarding?: boolean;
		};
	};
	/**
	 * Empresa del usuario, resuelta por el backend desde `companies` vía
	 * `users.company_id`. `null` si no tiene empresa (B2C / autónomo puro).
	 * Sustituye a `user.companyName` / `user.companyCif`.
	 */
	company: {
		id: string;
		/** Nullable: `companies.name` lo es en BBDD y el backend emite `name ?? null`. */
		name: string | null;
		NIF: string | null;
	} | null;
	/**
	 * ⚠️ OPCIONAL desde la tarea 06/15 de comparsa: una `user_epp_config` existe
	 * si y solo si el usuario tiene plan, así que el backend omite esta clave para
	 * el invitado que todavía no ha aceptado. Declararla obligatoria hacía que el
	 * compilador aceptara `userEppConfig.data.x` en cualquier parte, y para ese
	 * usuario eso revienta la primera pantalla que ve.
	 */
	userEppConfig?: {
		id: string;
		createdAt: string;
		updatedAt: string;
		userId: string;
		status: string;
		data?: {
			contributeAlwaysMax: boolean;
			fondo: string;
			plan: string;
			subplan: string;
			pensionContribution: number;
			contributionPeriodicity: string;
			freelanceContributionType: ContributionTypeEnum;
			freelanceContributionDay: number;
			freelanceContributionPeriodicity: ContributionPeriodicity;
			employeeContributionType: ContributionTypeEnum;
			employeeCategory: EmployeeCategory;
		};
		lastContributionDate: string;
		linkRegulation: string;
	};
	flexibleCompensationUserData?: {
		id: string;
		createdAt: string;
		updatedAt: string;
		userId: string;
		usesFlexibleCompensation: boolean;
		acceptsCompanyHealthInsurance: boolean;
		usesTransport: boolean;
		usesFood: boolean;
		usesChildcare: boolean;
		childcareMonthlyAmount: number | null;
		usesTraining: boolean;
		trainingAnnualAmount: number | null;
	} | null;
	flexibleCompensationCompanyConfig?: {
		id: string;
		createdAt: string;
		updatedAt: string;
		companyId: string;
		hasCompanyHealthInsurance: boolean;
		companyHealthInsuranceAmount: string;
		transportAvailable: boolean;
		transportAmount: string;
		foodAvailable: boolean;
		foodAmount: string;
		childcareAvailable: boolean;
		childcareAmount: string;
		trainingAvailable: boolean;
		trainingAmount: string;
	} | null;
	/**
	 * Calculated employer contribution based on company EPP configuration.
	 * For companies with collectiveAgreement, this is calculated from the user's collectiveAgreementGroup.
	 * Returns the monthly amount in euros.
	 */
	calculatedEmployerContribution?: number;
}

const EMPTY_CURRENT_USER: CurrentUserI = {
	user: {
		id: '',
		createdAt: '',
		updatedAt: '',
		isFreelance: false,
		email: '',
		password: '',
		name: '',
		phone: '',
		roles: [],
		firstSurname: '',
		lastSurname: '',
		gender: UserGenderEnum.MEN,
		language: '',
		nationality: '',
		country: '',
		nif: '',
		birthdate: '',
		street: '',
		zip: '',
		province: '',
		population: '',
		bankAccount: '',
		professionalActivity: '',
		lastLogin: '',
		status: '',
		companyId: '',
		representsCompany: false,
		representsParticipant: false,
		completedOnboarding: false,
		completedProfile: false,
		grossAnnualSalary: 0,
		lastMovementsUpdate: '',
		noWill: false,
		employmentStatus: UserEmploymentStatus.CURRENTLY_EMPLOYED,
		planStatus: UserPlanStatus.ACTIVE_IN_ARCA,
		isIndividual: false,
		employmentStartDate: '',
		data: {
			showExtendedOnboarding: false
		}
	},
	company: null,
	userEppConfig: {
		id: '',
		createdAt: '',
		updatedAt: '',
		userId: '',
		status: '',
		data: {
			contributeAlwaysMax: false,
			fondo: '',
			plan: '',
			subplan: '',
			pensionContribution: 0,
			contributionPeriodicity: '',
			freelanceContributionType: ContributionTypeEnum.FIXED,
			freelanceContributionDay: 0,
			freelanceContributionPeriodicity: ContributionPeriodicity.MONTHLY,
			employeeContributionType: ContributionTypeEnum.FIXED,
			employeeCategory: EmployeeCategory.NON_DIRECTOR
		},
		lastContributionDate: '',
		linkRegulation: ''
	},
	flexibleCompensationUserData: null,
	flexibleCompensationCompanyConfig: null
};

export default EMPTY_CURRENT_USER;
